import { defineBlock, cssText, defaultPipeFor, resolveValue, toNumber, toDisplayString, compareValues, getField, isDarkColor, withDefaultPipe, type FieldSchema, type Issue, type VNode } from "@broadpaper/core";

export interface TableColumn {
  id: string;
  header: string;
  /** Expression evaluated per row, e.g. `holding.value | currency`. */
  value: string;
  align?: "left" | "center" | "right";
  /** Width as a percentage of the table width; null = auto (equal share of the rest). */
  width?: number | null;
  bold?: boolean;
  /** Summary function for the totals row. */
  total?: "none" | "sum" | "avg" | "count" | "min" | "max";
  /** Expression for the totals cell (overrides `total`), evaluated in the table scope with `rows` available. */
  totalExpr?: string;
  /** Formatting pipe applied to the total, e.g. `currency:"GBP"`. */
  totalFormat?: string;
  wrap?: boolean;
}

export interface ConditionalFormat {
  id: string;
  /** Column id, or "*" for the whole row. */
  columnId: string;
  /** Expression evaluated per row. */
  condition: string;
  color?: string;
  background?: string;
  bold?: boolean;
}

export interface TableProps extends Record<string, unknown> {
  source: string;
  alias: string;
  columns: TableColumn[];
  showHeader: boolean;
  repeatHeader: boolean;
  zebra: boolean;
  borders: "inherit" | "all" | "horizontal" | "outer" | "none";
  compact: boolean;
  fontSize: number | null;
  headerBackground: string;
  headerColor: string;
  sortBy: string;
  sortDir: "asc" | "desc";
  emptyMessage: string;
  totalsLabel: string;
  conditionalFormats: ConditionalFormat[];
  designRows: number;
  rowLimit: number | null;
}

interface Cell {
  text: string;
  style?: Record<string, string>;
}

interface ResolvedRows {
  __rows: Array<{ cells: Cell[]; style?: Record<string, string> }>;
  __totals: Cell[] | null;
  __rowCount: number;
  /** Rows actually drawn, which in design mode is a sample of the whole. */
  __rowsShown: number;
}

function defaultColumns(): TableColumn[] {
  return [
    { id: "c1", header: "Name", value: "row.name", width: null },
    { id: "c2", header: "Value", value: "row.value | number", align: "right", width: 30, total: "sum" }
  ];
}

/**
 * The table a list turns into when it is dropped on the page.
 *
 * One column per field of the item schema, in declared order, each with the
 * heading the schema gave it, the formatter its type implies, numbers right
 * aligned and money totalled. The alternative — an empty table and a trip to the
 * inspector to add every column by hand — is a chore for a developer and a wall
 * for anybody else, and the information needed to skip it was in the schema all
 * along.
 *
 * Nothing here invents a column: only fields the schema declares, and no nested
 * objects or lists, because a cell cannot hold either.
 */
function columnsFromItems(fields: Record<string, FieldSchema>, alias: string): TableColumn[] {
  const usable = Object.entries(fields).filter(([, f]) => f.type !== "object" && f.type !== "array");
  const columns: TableColumn[] = [];
  let i = 0;
  for (const [key, schema] of usable) {
    i++;
    const numeric = schema.type === "number" || schema.type === "currency" || schema.type === "percent";
    const column: TableColumn = {
      id: `c${i}`,
      header: schema.label ?? key,
      value: withDefaultPipe(`${alias}.${key}`, schema),
      // Widths are left automatic on purpose. A set of widths that happens to
      // total exactly 100 trips the engine's own overflow check, and equal
      // shares of the remainder is a better starting point than arithmetic
      // nobody asked for.
      width: null
    };
    if (numeric) column.align = "right";
    // Money and counts are what a reader looks for at the foot of a table; a
    // percentage summed is meaningless and is deliberately not totalled.
    if (schema.type === "currency" || schema.type === "number") {
      column.total = "sum";
      const pipe = defaultPipeFor(schema);
      if (pipe) column.totalFormat = pipe;
    }
    columns.push(column);
  }
  // A schema with nothing usable in it still has to produce a table somebody can
  // work with rather than one with no columns at all.
  return columns.length ? columns.slice(0, 12) : defaultColumns();
}

/**
 * A singular row name from a list's label: `Holdings` → `holding`, `Line items`
 * → `lineItem`.
 *
 * Only the last word is used, and only the commonest plural endings are undone.
 * Anything it cannot make sense of stays `row`, which is always correct and
 * never surprising — guessing badly here puts a wrong-looking alias into every
 * column expression in the table.
 */
function aliasFromLabel(label: string): string {
  const last = label.split(" › ").pop() ?? label;
  const words = last
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  if (!words.length) return "row";
  // `sses` and not `ses`: "classes" is a class, but "expenses" is an expense.
  const singular = (w: string) => (/ies$/i.test(w) ? `${w.slice(0, -3)}y` : /(sses|xes|zes|ches|shes)$/i.test(w) ? w.slice(0, -2) : /[^s]s$/i.test(w) ? w.slice(0, -1) : w);
  const parts = [...words.slice(0, -1), singular(words[words.length - 1]!)].map((w, i) =>
    i === 0 ? w.toLowerCase() : w[0]!.toUpperCase() + w.slice(1).toLowerCase()
  );
  const alias = parts.join("");
  return /^[A-Za-z_$][\w$]*$/.test(alias) ? alias : "row";
}

/**
 * The name the designer gave each row, which is what their column expressions
 * are written against. Hard-coding "row" in the help text was wrong for every
 * table that renamed it — and the demo's holdings table calls its rows `h`.
 */
function aliasOf(props: Record<string, unknown>): string {
  const alias = props.alias;
  return typeof alias === "string" && alias.trim() ? alias.trim() : "row";
}

/** A column's heading, for places a person reads rather than the id it is keyed by. */
function columnName(id: string, props: Record<string, unknown>): string {
  if (!id || id === "*") return "Every column";
  const column = ((props.columns as TableColumn[]) ?? []).find((c) => c.id === id);
  return column?.header || column?.value || id;
}

export const tableBlock = defineBlock<TableProps>({
  type: "table",
  name: "Table",
  description: "Data table bound to a list, with totals, zebra rows and page-spanning headers",
  category: "data",
  icon: "table",
  keywords: ["grid", "rows", "columns", "data", "list"],
  defaultProps: () => ({
    source: "",
    alias: "row",
    columns: defaultColumns(),
    showHeader: true,
    repeatHeader: true,
    zebra: true,
    borders: "inherit",
    compact: false,
    fontSize: null,
    headerBackground: "",
    headerColor: "",
    sortBy: "",
    sortDir: "asc",
    emptyMessage: "No data",
    totalsLabel: "Total",
    conditionalFormats: [],
    designRows: 3,
    rowLimit: null
  }),
  defaultTextStyle: "body",
  defaultFlow: { minOrphans: 2, minWidows: 2 },
  split: "rows",
  measure: "table",
  styleGroups: ["typography", "spacing", "flow"],
  bindings: [
    { prop: "source", kind: "collection", alias: "alias" },
    { prop: "columns", kind: "list", items: [{ prop: "value", kind: "value", perItem: true }, { prop: "header", kind: "template" }, { prop: "totalExpr", kind: "value" }] },
    { prop: "conditionalFormats", kind: "list", items: [{ prop: "condition", kind: "value", perItem: true }] },
    { prop: "emptyMessage", kind: "template" },
    { prop: "totalsLabel", kind: "template" }
  ],
  inspector: [
    {
      title: "Data",
      fields: [
        { kind: "collection", prop: "source", label: "Rows from", placeholder: "Pick a list" },
        { kind: "text", prop: "alias", label: "Row name", description: (p) => `The name each row goes by in column values, e.g. ${aliasOf(p)}.name`, allowTokens: false },
        { kind: "text", prop: "sortBy", label: "Sort by field", placeholder: "e.g. value", allowTokens: false },
        {
          kind: "segmented",
          prop: "sortDir",
          label: "Direction",
          options: [
            { value: "asc", label: "Ascending" },
            { value: "desc", label: "Descending" }
          ],
          when: (p) => !!p.sortBy
        },
        { kind: "number", prop: "rowLimit", label: "Maximum rows", min: 1, max: 100000 },
        { kind: "text", prop: "emptyMessage", label: "Message when empty", description: "Printed instead of the table when the list has no rows.", fullWidth: true }
      ]
    },
    { title: "Columns", fields: [{ kind: "columns", prop: "columns", label: "Columns", fullWidth: true }] },
    {
      title: "Appearance",
      collapsed: true,
      fields: [
        { kind: "toggle", prop: "showHeader", label: "Header row" },
        { kind: "toggle", prop: "repeatHeader", label: "Repeat header on each page", when: (p) => p.showHeader !== false },
        { kind: "toggle", prop: "zebra", label: "Zebra rows" },
        { kind: "toggle", prop: "compact", label: "Compact rows" },
        {
          kind: "select",
          prop: "borders",
          label: "Borders",
          options: [
            { value: "inherit", label: "Document default" },
            { value: "horizontal", label: "Horizontal lines" },
            { value: "all", label: "Full grid" },
            { value: "outer", label: "Outer only" },
            { value: "none", label: "None" }
          ]
        },
        { kind: "color", prop: "headerBackground", label: "Header background", allowTransparent: true },
        { kind: "color", prop: "headerColor", label: "Header text" },
        { kind: "number", prop: "fontSize", label: "Font size", min: 6, max: 24, unit: "px" },
        { kind: "text", prop: "totalsLabel", label: "Totals label" }
      ]
    },
    {
      title: "Conditional formatting",
      collapsed: true,
      fields: [
        {
          kind: "list",
          prop: "conditionalFormats",
          label: "Rules",
          itemLabel: "Rule",
          fullWidth: true,
          // A rule reads as "Drift · when abs(a.drift) > 0.03" rather than "c5",
          // which is an internal id the designer never chose and cannot look up.
          itemSummary: (rule, i, props) => {
            const column = columnName(String(rule.columnId ?? ""), props);
            const when = String(rule.condition ?? "").trim();
            return when ? `${column} · when ${when}` : `${column} · Rule ${i + 1}`;
          },
          fields: [
            {
              kind: "select",
              prop: "columnId",
              label: "Column",
              optionsFrom: (props) => [{ value: "*", label: "Every column" }, ...((props.columns as TableColumn[]) ?? []).map((c) => ({ value: c.id, label: c.header || c.id }))]
            },
            { kind: "expression", prop: "condition", label: "When", placeholder: "row.value < 0", description: (p) => `Checked once per row, e.g. ${aliasOf(p)}.value < 0` },
            { kind: "color", prop: "color", label: "Text colour" },
            { kind: "color", prop: "background", label: "Background", allowTransparent: true },
            { kind: "toggle", prop: "bold", label: "Bold" }
          ]
        }
      ]
    }
  ],
  validate: ({ props, checkExpression }) => {
    const issues: Issue[] = [];
    if (!Array.isArray(props.columns) || props.columns.length === 0) issues.push({ severity: "warning", code: "empty-content", message: "Table has no columns", prop: "columns" });
    for (const c of (props.columns as TableColumn[]) ?? []) if (!c.value?.trim()) issues.push({ severity: "warning", code: "missing-required", message: `Column "${c.header || c.id}" has no value`, prop: `columns.${c.id}.value` });
    for (const c of (props.columns as TableColumn[]) ?? []) if (c.totalExpr?.trim()) issues.push(...checkExpression(c.totalExpr, `columns.${c.id}.totalExpr`));
    return issues;
  },
  /**
   * Dropping a list on the page gives a finished table, not an empty one.
   *
   * The alias is derived from the field's own name so the column expressions
   * read as prose — `holding.value` rather than `row.value` — which is what
   * somebody looking at the inspector afterwards can actually follow.
   */
  fromField: ({ expr, schema, label }) => {
    if (schema.type !== "array") return undefined;
    const alias = aliasFromLabel(label);
    const fields = schema.itemSchema?.type === "object" ? schema.itemSchema.fields : undefined;
    return { source: expr, alias, columns: fields ? columnsFromItems(fields, alias) : defaultColumns() };
  },

  canvas: { label: (p) => (p.source ? `Table · ${p.source}` : "Table") },
  resolve: ({ props, mode, evaluateIn, evaluate, warn, theme }) => {
    const alias = props.alias || "row";
    let items = Array.isArray(props.source) ? (props.source as unknown[]) : [];
    if (props.sortBy) {
      const dir = props.sortDir === "desc" ? -1 : 1;
      items = [...items].sort((a, b) => compareValues(getField(a, props.sortBy), getField(b, props.sortBy)) * dir);
    }
    const total = items.length;
    if (props.rowLimit) items = items.slice(0, props.rowLimit);
    // Totals always cover the full data set; design mode only shows a few rows.
    const allItems = items;
    if (mode === "design") items = items.slice(0, Math.max(1, props.designRows || 3));
    const columns = Array.isArray(props.columns) ? props.columns : [];
    const formats = Array.isArray(props.conditionalFormats) ? props.conditionalFormats : [];

    const rows = items.map((item, index) => {
      const vars = { [alias]: item, item, row: item, index: index + 1, first: index === 0, last: index === items.length - 1, count: items.length };
      const rowStyle: Record<string, string> = {};
      const cellStyles: Record<string, Record<string, string>> = {};
      for (const f of formats) {
        if (!f.condition?.trim()) continue;
        let ok = false;
        try {
          ok = !!evaluateIn(f.condition, vars);
        } catch {
          ok = false;
        }
        if (!ok) continue;
        const st: Record<string, string> = {};
        if (f.color) st["color"] = resolveValue(f.color, theme, theme.colors.text);
        if (f.background) st["background"] = resolveValue(f.background, theme, "transparent");
        if (f.bold) st["font-weight"] = "700";
        if (f.columnId === "*" || !f.columnId) Object.assign(rowStyle, st);
        else cellStyles[f.columnId] = { ...(cellStyles[f.columnId] ?? {}), ...st };
      }
      const cells: Cell[] = columns.map((c) => {
        let text = "";
        if (c.value?.trim()) {
          try {
            text = toDisplayString(evaluateIn(c.value, vars));
          } catch (e) {
            warn(`Column "${c.header}" failed: ${(e as Error).message}`);
          }
        }
        return { text, style: cellStyles[c.id] };
      });
      return { cells, style: Object.keys(rowStyle).length ? rowStyle : undefined };
    });

    let totals: Cell[] | null = null;
    if (columns.some((c) => (c.total && c.total !== "none") || c.totalExpr?.trim())) {
      totals = columns.map((c, i) => {
        if (c.totalExpr?.trim()) {
          try {
            return { text: toDisplayString(evaluateIn(c.totalExpr, { rows: allItems, [alias + "s"]: allItems })) };
          } catch {
            return { text: "" };
          }
        }
        if (!c.total || c.total === "none") return { text: i === 0 && !columns[0]?.total ? String(props.totalsLabel ?? "Total") : "" };
        const values = allItems.map((it) => toNumber(evaluateIn(stripPipes(c.value), { [alias]: it, item: it, row: it }))).filter((n): n is number => n !== null);
        let n: number | null = null;
        if (c.total === "sum") n = values.reduce((a, b) => a + b, 0);
        else if (c.total === "avg") n = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
        else if (c.total === "count") n = allItems.length;
        else if (c.total === "min") n = values.length ? Math.min(...values) : null;
        else if (c.total === "max") n = values.length ? Math.max(...values) : null;
        const fmt = c.totalFormat?.trim() || extractPipes(c.value);
        const text = n === null ? "" : fmt ? toDisplayString(evaluate(`(${n}) | ${fmt}`)) : toDisplayString(n);
        return { text };
      });
      if (totals[0] && totals[0].text === "" && !(columns[0]?.total && columns[0].total !== "none")) totals[0] = { text: String(props.totalsLabel ?? "Total") };
    }

    return { __rows: rows, __totals: totals, __rowCount: total, __rowsShown: rows.length } as unknown as TableProps & ResolvedRows;
  },
  render: ({ h, css, props, theme, styles, mode, text, node, style }) => {
    const p = props as TableProps & Partial<ResolvedRows>;
    const columns = Array.isArray(p.columns) ? p.columns : [];
    const ts = styles.table;
    const fontSize = p.fontSize ?? ts.fontSize ?? style.fontSize;
    const borders = p.borders === "inherit" ? ts.borders ?? "horizontal" : p.borders;
    const borderColor = resolveValue(ts.borderColor, theme, theme.colors.border);
    const bw = ts.borderWidth ?? 1;
    const padY = p.compact ? Math.max(2, (ts.cellPaddingY ?? 6) - 3) : ts.cellPaddingY ?? 6;
    const padX = ts.cellPaddingX ?? 8;
    const headerBg = p.headerBackground ? resolveValue(p.headerBackground, theme, "") : resolveValue(ts.headerBackground, theme, theme.table?.headerBackground ?? "#F3F4F6");
    const headerColor = p.headerColor ? resolveValue(p.headerColor, theme, "") : resolveValue(ts.headerColor, theme, headerBg && isDarkColor(headerBg) ? "#FFFFFF" : theme.colors.text);
    const zebraBg = resolveValue(ts.zebraBackground, theme, theme.table?.zebraBackground ?? "#FAFAFB");

    const cellBorder = (isHeader: boolean, last: boolean): Record<string, string> => {
      const line = `${bw}px solid ${borderColor}`;
      switch (borders) {
        case "all":
          return { border: line };
        case "horizontal":
          return { "border-bottom": last ? line : line, ...(isHeader ? { "border-bottom": `${bw * 2}px solid ${borderColor}` } : {}) };
        case "outer":
          return {};
        default:
          return {};
      }
    };

    const rows = p.__rows ?? [];
    const rowsVNodes: VNode[] = rows.map((r, i) =>
      h(
        "tr",
        { class: ["bp-table__row", p.zebra && i % 2 === 1 && "bp-table__row--alt"], style: { ...(p.zebra && i % 2 === 1 ? { background: zebraBg } : {}), ...(r.style ?? {}) } },
        r.cells.map((c, ci) => {
          const col = columns[ci];
          return h("td", { class: ["bp-table__cell", mode === "design" && "bp-cell--bound"], style: { padding: `${padY}px ${padX}px`, "text-align": col?.align ?? "left", "font-weight": col?.bold ? "600" : undefined, "vertical-align": "top", "white-space": col?.wrap === false ? "nowrap" : "normal", overflow: "hidden", "text-overflow": "ellipsis", ...cellBorder(false, i === rows.length - 1), ...(c.style ?? {}) } }, c.text);
        })
      )
    );

    const specified = columns.reduce((a, c) => a + (c.width ?? 0), 0);
    const autoCount = columns.filter((c) => !c.width).length;
    const autoWidth = autoCount ? Math.max(5, (100 - specified) / autoCount) : 0;
    const colgroup = h("colgroup", null, columns.map((c) => h("col", { style: { width: `${c.width ?? autoWidth}%` } })));

    const thead = p.showHeader !== false
      ? h("thead", { class: "bp-table__head" }, h("tr", null, columns.map((c) => h("th", { class: "bp-table__th", style: { padding: `${padY}px ${padX}px`, "text-align": c.align ?? "left", background: headerBg || undefined, color: headerColor, "font-weight": String(ts.headerFontWeight ?? 600), "vertical-align": "bottom", ...cellBorder(true, false) } }, ...text(`columns.${columns.indexOf(c)}.header`, c.header)))))
      : null;

    const tfoot = p.__totals
      ? h("tfoot", { class: "bp-table__foot" }, h("tr", null, p.__totals.map((c, ci) => h("td", { class: "bp-table__total", style: { padding: `${padY}px ${padX}px`, "text-align": columns[ci]?.align ?? "left", "font-weight": "700", "border-top": `${bw * 2}px solid ${borderColor}`, ...(borders === "all" ? { border: `${bw}px solid ${borderColor}` } : {}) } }, c.text))))
      : null;

    const empty = rows.length === 0;
    const tableStyle: Record<string, string | undefined> = { width: "100%", "border-collapse": "collapse", "table-layout": "fixed", "font-size": `${fontSize}px`, ...(borders === "outer" ? { border: `${bw}px solid ${borderColor}` } : {}) };
    const b = node.bindings.source;
    return h(
      "div",
      { class: "bp-table-wrap", style: cssText(css) },
      // Design mode draws a handful of rows to keep the canvas quick, which is
      // indistinguishable from a table that only has a handful of rows unless
      // the badge says so.
      mode === "design" ? h("div", { class: "bp-table__badge", contenteditable: "false" }, b ? `${b.label} · ${rowSampleNote(p.__rowsShown ?? 0, p.__rowCount ?? 0)}` : "Choose a list") : null,
      h("table", { class: "bp-table", style: tableStyle }, colgroup, thead, h("tbody", null, empty ? h("tr", null, h("td", { colspan: String(Math.max(1, columns.length)), style: { padding: `${padY * 2}px ${padX}px`, color: theme.colors.mutedText, "text-align": "center", "font-style": "italic" } }, ...text("emptyMessage", "No data"))) : rowsVNodes), tfoot)
    );
  }
});

/** "Showing 3 of 7 sample rows", or just "7 rows" when nothing is being held back. */
function rowSampleNote(shown: number, total: number): string {
  if (total === 0) return "no rows";
  if (shown >= total) return `${total} row${total === 1 ? "" : "s"}`;
  return `showing ${shown} of ${total} sample rows`;
}

function stripPipes(expr: string): string {
  // Remove trailing formatter pipes for numeric aggregation: `row.value | currency` → `row.value`
  const idx = expr.indexOf("|");
  if (idx < 0) return expr;
  // Ignore `||` which the parser treats as "or".
  if (expr[idx + 1] === "|") return expr;
  return expr.slice(0, idx).trim();
}

function extractPipes(expr: string): string {
  const idx = expr.indexOf("|");
  if (idx < 0 || expr[idx + 1] === "|") return "";
  return expr.slice(idx + 1).trim();
}

export const keyValueListBlock = defineBlock<{ items: Array<{ id: string; label: string; value: string }>; layout: "inline" | "stacked" | "grid"; labelWidth: number; dividers: boolean; columns: number }>({
  type: "keyValueList",
  name: "Key / value list",
  description: "A list of labelled values",
  category: "data",
  icon: "list",
  keywords: ["details", "properties", "definition list", "summary"],
  defaultProps: () => ({
    items: [
      { id: "k1", label: "Name", value: "" },
      { id: "k2", label: "Reference", value: "" }
    ],
    layout: "inline",
    labelWidth: 35,
    dividers: true,
    columns: 1
  }),
  defaultTextStyle: "body",
  split: "none",
  styleGroups: ["typography", "spacing", "background", "border", "flow"],
  bindings: [{ prop: "items", kind: "list", items: [{ prop: "label", kind: "template" }, { prop: "value", kind: "template" }] }],
  inspector: [
    {
      title: "Items",
      fields: [
        { kind: "keyValues", prop: "items", label: "Items", fullWidth: true },
        {
          kind: "segmented",
          prop: "layout",
          label: "Layout",
          options: [
            { value: "inline", label: "Inline" },
            { value: "stacked", label: "Stacked" },
            { value: "grid", label: "Grid" }
          ]
        },
        { kind: "number", prop: "columns", label: "Columns", min: 1, max: 4, when: (p) => p.layout === "grid" },
        { kind: "number", prop: "labelWidth", label: "Label width", min: 10, max: 80, unit: "%", when: (p) => p.layout === "inline" },
        { kind: "toggle", prop: "dividers", label: "Dividers" }
      ]
    }
  ],
  render: ({ h, css, props, theme, text }) => {
    const items = Array.isArray(props.items) ? props.items : [];
    const divider = props.dividers ? `1px solid ${theme.colors.border}` : undefined;
    if (props.layout === "grid") {
      return h(
        "div",
        { class: "bp-kv bp-kv--grid", style: cssText({ ...css, display: "grid", "grid-template-columns": `repeat(${props.columns || 2}, minmax(0, 1fr))`, gap: "10px 16px" }) },
        items.map((it, i) => h("div", { class: "bp-kv__item" }, h("div", { class: "bp-kv__label", style: { color: theme.colors.mutedText, "font-size": "0.85em", "text-transform": "uppercase", "letter-spacing": "0.04em", "font-weight": "600" } }, text(`items.${i}.label`, it.label)), h("div", { class: "bp-kv__value", style: { "font-weight": "500" } }, text(`items.${i}.value`, it.value || "—"))))
      );
    }
    return h(
      "div",
      { class: ["bp-kv", `bp-kv--${props.layout}`], style: cssText(css) },
      items.map((it, i) =>
        props.layout === "inline"
          ? h("div", { class: "bp-kv__row", style: { display: "flex", gap: "8px", padding: "5px 0", "border-bottom": i < items.length - 1 ? divider : undefined } }, h("div", { class: "bp-kv__label", style: { flex: `0 0 ${props.labelWidth}%`, color: theme.colors.mutedText } }, text(`items.${i}.label`, it.label)), h("div", { class: "bp-kv__value", style: { flex: "1", "min-width": "0", "font-weight": "500" } }, text(`items.${i}.value`, it.value || "—")))
          : h("div", { class: "bp-kv__row", style: { padding: "5px 0", "border-bottom": i < items.length - 1 ? divider : undefined } }, h("div", { class: "bp-kv__label", style: { color: theme.colors.mutedText, "font-size": "0.85em" } }, text(`items.${i}.label`, it.label)), h("div", { class: "bp-kv__value", style: { "font-weight": "500" } }, text(`items.${i}.value`, it.value || "—")))
      )
    );
  }
});
